import { forwardRef } from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { Icon } from '@winnin/ds-icon'

import { CopyPasteIcon } from 'assets/icons'
import { BaseSkeleton } from 'components/BaseSkeleton'
import { RCTooltip } from 'components/RCTooltip'
import TerritoryAddToCheckbox from 'components/TerritoryAddToCheckbox'
import { useTranslate } from 'lib/tolgee'
import { humanizeLargeNumber, TOLGEE_GLOBALS } from 'utils'

import type { TNicheCardView } from './niche-card.types'

import './niche-card.styles.scss'

export const NicheCardView = forwardRef(
  (
    {
      engagement,
      name,
      isLoading,
      isVisible,
      handleClick,
      territoryId,
      shouldDisplayCopyTerritoryQuery,
      handleCopyTerritoryQuery,
      isCopyTerritoryQueryDisabled,
      eyeIconTooltipMessage,
      color,
      handleTrackClick,
      targetUrl,
      checkboxEntityType,
      checkboxEntityObjectType,
      checkboxId,
      hideAddToCheckbox,
      hideRedirectLink,
    }: TNicheCardView,
    ref: any
  ) => {
    const t = useTranslate()

    return (
      <div className="NicheCard" ref={ref}>
        <div className="NicheCard-leftSection">
          <div className="NicheCard-colorBar" style={{ backgroundColor: color }} />
          {!hideAddToCheckbox && (
            <div
              className={classNames('NicheCard-checkboxSection', {
                'last-child-no-margin': shouldDisplayCopyTerritoryQuery,
              })}
            >
              <TerritoryAddToCheckbox
                entityType={checkboxEntityType}
                id={checkboxId}
                checkboxSize={16}
                entityObject={{
                  id: checkboxId,
                  name,
                  type: checkboxEntityObjectType,
                }}
              />
            </div>
          )}
        </div>
        <div className="NicheCard-titleSection">
          <RCTooltip tooltipMessage={name}>
            {hideRedirectLink || isLoading ? (
              <span className="NicheCard-title">{name}</span>
            ) : (
              <Link
                to={targetUrl}
                target="_blank"
                rel="noreferrer"
                className="NicheCard-title"
                onClick={() => handleTrackClick({ name })}
              >
                {name}
              </Link>
            )}
          </RCTooltip>
          {isLoading ? (
            <BaseSkeleton height="12px" width="50px" borderRadius="12px" />
          ) : (
            <span className="NicheCard-subtitle">
              {humanizeLargeNumber(engagement)} {t(TOLGEE_GLOBALS.engagement)}
            </span>
          )}
        </div>
        <div className="NicheCard-rightContent">
          {shouldDisplayCopyTerritoryQuery && (
            <div className="NicheCard-copyQuerySection">
              <RCTooltip direction="bottom" tooltipMessage={t(TOLGEE_GLOBALS.copyTerritoryQuery)}>
                <button
                  onClick={handleCopyTerritoryQuery}
                  disabled={isCopyTerritoryQueryDisabled}
                  aria-label={t(TOLGEE_GLOBALS.copyTerritoryQuery)}
                >
                  <CopyPasteIcon />
                </button>
              </RCTooltip>
            </div>
          )}
          <div className={classNames(['NicheCard-iconSection', { 'NicheCard-shouldAppear': !isVisible }])}>
            <RCTooltip direction="bottom" tooltipMessage={eyeIconTooltipMessage}>
              <button
                onClick={event => {
                  event?.preventDefault()
                  handleClick(territoryId)
                }}
              >
                <Icon name={isVisible ? 'eye' : 'eyeOff'} />
              </button>
            </RCTooltip>
          </div>
        </div>
      </div>
    )
  }
)

NicheCardView.displayName = 'NicheCardView'
